import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useInnerCircle } from '../context/InnerCircleContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import OrnateDivider from '../components/OrnateDivider';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (_e) { return iso; }
};

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between py-2 border-b border-primary/10 last:border-b-0">
    <span className="text-[11px] font-heading tracking-[0.25em] text-primary/60 uppercase">{k}</span>
    <span className="text-sm text-primary/90">{v}</span>
  </div>
);

const MembershipPage = () => {
  const { user, refreshUser } = useAuth();
  const { openIC } = useInnerCircle();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('open') === '1' && !user?.is_member) openIC();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.is_member]);

  useEffect(() => {
    (async () => {
      if (!user) {
        navigate('/auth?next=/account/membership');
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/membership/me`, { headers: { Authorization: `Bearer ${token}` } });
        setData(r.data);
      } catch (e) {
        toast.error('Could not load your membership.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const cancel = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/membership/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Auto-renewal turned off. Your access continues until the period end.');
      setData({ membership: r.data.user.membership, is_member: r.data.user.is_member });
      await refreshUser?.();
      setCancelOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not turn off auto-renewal.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen pt-32 text-center text-primary/60">Loading…</div>;

  const m = data?.membership || {};
  const isMember = data?.is_member || user?.is_member;

  // Non-members: show popup entry
  if (!isMember) {
    return (
      <div className="min-h-screen pt-28 pb-20 px-4" data-testid="membership-page-nonmember">
        <div className="max-w-xl mx-auto text-center">
          <OrnateDivider variant="lotus" className="mb-6 max-w-md mx-auto" />
          <h1 className="text-3xl sm:text-4xl font-heading golden-text text-glow mb-4">The Inner Circle</h1>
          <p className="text-sm text-primary/75 italic mb-8">
            Deeper teachings from the Siddha tradition, shared with those who wish to go further.
          </p>
          <Button
            onClick={openIC}
            className="ornate-button px-8 py-3 text-sm font-heading uppercase tracking-widest"
            data-testid="membership-open-popup"
          >
            Join the Inner Circle
          </Button>
        </div>
      </div>
    );
  }

  const planLabel = m.plan === 'yearly' ? 'Yearly · ₹1,100'
    : m.plan === 'monthly' ? 'Monthly · ₹108'
    : m.plan === 'granted' ? 'Granted'
    : m.plan === 'admin' ? 'Admin'
    : '—';

  const statusLabel =
    m.status === 'active' ? 'Active'
    : m.status === 'cancelling' ? 'Auto-renewal off'
    : m.status === 'expired' ? 'Expired'
    : m.status;

  return (
    <div className="min-h-screen pt-28 pb-20 px-4" data-testid="membership-page">
      <div className="max-w-xl mx-auto">
        <OrnateDivider variant="lotus" className="mb-6 max-w-md mx-auto" />
        <h1 className="text-3xl sm:text-4xl font-heading golden-text text-glow text-center mb-2">
          Inner Circle · Membership
        </h1>
        <p className="text-xs tracking-[0.3em] uppercase text-primary/55 text-center mb-8">
          Namaste, {user?.name?.split(' ')[0] || 'friend'}
        </p>

        <div className="card-mystical rounded-2xl p-6 sm:p-8">
          <Row k="Status" v={<span data-testid="ms-status">{statusLabel}</span>} />
          <Row k="Plan" v={<span data-testid="ms-plan">{planLabel}</span>} />
          <Row k="Started" v={fmt(m.start_date)} />
          <Row
            k={m.status === 'cancelling' ? 'Access until' : 'Next renewal'}
            v={<span data-testid="ms-next">{fmt(m.period_end)}</span>}
          />
        </div>

        {/* Cancel / renew */}
        {m.status === 'active' && m.auto_renew && m.source === 'paid' && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="text-xs uppercase tracking-[0.3em] text-primary/60 hover:text-primary"
              data-testid="ms-turn-off"
            >
              Turn off auto-renewal
            </button>
          </div>
        )}

        {m.status === 'cancelling' && (
          <p
            className="mt-6 text-sm text-primary/80 italic text-center"
            data-testid="ms-cancel-notice"
          >
            Auto-renewal is off. Your access continues until {fmt(m.period_end)}.
          </p>
        )}

        {m.status === 'expired' && (
          <div className="mt-6 text-center">
            <Button onClick={openIC} className="ornate-button px-8 py-3 text-sm uppercase tracking-widest" data-testid="ms-renew">
              Renew
            </Button>
          </div>
        )}

        {m.source === 'granted' && (
          <p className="mt-6 text-xs text-primary/55 italic text-center">
            This is a complimentary membership from the Samaj. Access until {fmt(m.period_end)}.
          </p>
        )}
      </div>

      {/* Confirmation */}
      {cancelOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="ms-confirm-cancel">
          <button className="absolute inset-0 bg-black/75" onClick={() => setCancelOpen(false)} aria-label="Close" />
          <div className="relative card-mystical rounded-2xl max-w-sm w-full p-6 text-center">
            <h3 className="text-lg font-heading golden-text mb-3">Turn off auto-renewal?</h3>
            <p className="text-sm text-primary/80 italic mb-5 leading-relaxed">
              Your access will continue until {fmt(m.period_end)}. You will not be charged again, and you can renew any time.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={() => setCancelOpen(false)}
                className="px-5 py-2 text-xs uppercase tracking-widest rounded-lg border border-primary/30 text-primary/70"
                data-testid="ms-confirm-cancel-no"
              >
                Keep membership
              </button>
              <Button
                onClick={cancel}
                disabled={busy}
                className="ornate-button px-5 py-2 text-xs uppercase tracking-widest"
                data-testid="ms-confirm-cancel-yes"
              >
                {busy ? 'Working…' : 'Turn off'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipPage;
